/**
 * SUSI — shared UI primitives. Loaded (deferred) on every page, right after theme.js.
 *
 *   <details-disclosure>   animated <details> accordion
 *   <modal-opener>         opens the <modal-dialog> named in data-modal
 *   <modal-dialog>         centred dialog (moves to <body> while open)
 *   <side-drawer>          off-canvas panel: data-side="start | end | bottom"
 *   <carousel-slider>      scroll-snap slider with arrows, dots, counter, drag
 *   <quantity-wrapper>     − / + stepper around a number input
 *   <toggle-target>        button that reveals / hides another element
 *   <link-dropdown>        small menu of links (sort by, blog filter)
 *
 * Also adds to window.theme:
 *   theme.SideDrawer, theme.ModalDialog, theme.DetailsDisclosure, theme.CarouselSlider  (base classes)
 *   theme.slideToggle(element, open)   height animation used by the accordions
 *   theme.showHint(message, anchor)    short lived bubble next to a control
 *
 * Markup contracts live in the "ui-primitives" contract document.
 */
(() => {
  const theme = window.theme;
  if (!theme || theme.SideDrawer) return;

  /* ---------------------------------------------------------------------- */
  /* Helpers                                                                 */
  /* ---------------------------------------------------------------------- */

  const motionAllowed = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  /** Next animation frame (or 60ms, whichever comes first: hidden tabs never get a frame). */
  const nextFrame = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => resolve());
      setTimeout(resolve, 60);
    });

  /** Longest transition (duration + delay) declared on an element, in ms. */
  const transitionTime = (element) => {
    const style = getComputedStyle(element);
    const toMs = (list) => list.split(',').map((part) => (parseFloat(part) || 0) * (/ms\s*$/.test(part.trim()) ? 1 : 1000));
    const durations = toMs(style.transitionDuration);
    const delays = toMs(style.transitionDelay);
    const total = Math.max(0, ...durations.map((duration, i) => duration + (delays[i % delays.length] || 0)));
    return total < 20 ? 0 : total;
  };

  const animations = new WeakMap();

  /**
   * Opens or closes an element by animating its height. The element is shown /
   * hidden with the `hidden` attribute unless `options.keepVisible` is set.
   * @param {HTMLElement} element
   * @param {boolean} open
   * @param {object} [options] - { duration: ms, keepVisible: boolean }
   * @returns {Promise<void>} Resolves when the animation has finished.
   */
  theme.slideToggle = (element, open, options = {}) => {
    const running = animations.get(element);
    const from = running || !open ? element.getBoundingClientRect().height : 0;
    if (running) running.cancel();

    if (open && !options.keepVisible) element.hidden = false;

    const finish = () => {
      if (!open && !options.keepVisible) element.hidden = true;
      element.style.overflow = '';
      animations.delete(element);
    };

    if (!motionAllowed() || typeof element.animate !== 'function') {
      finish();
      return Promise.resolve();
    }

    const to = open ? element.scrollHeight : 0;
    element.style.overflow = 'hidden';
    const animation = element.animate(
      { height: [`${from}px`, `${to}px`], opacity: open ? [from ? 1 : 0, 1] : [1, 0] },
      { duration: options.duration || 300, easing: 'cubic-bezier(0.3, 0.1, 0.2, 1)' }
    );
    animations.set(element, animation);

    return new Promise((resolve) => {
      animation.onfinish = () => {
        finish();
        resolve();
      };
      animation.oncancel = () => resolve();
    });
  };

  /* ---------------------------------------------------------------------- */
  /* <details-disclosure>                                                    */
  /* ---------------------------------------------------------------------- */
  /*
   * Wraps one <details>. Without JS (or with reduced motion) the native
   * element simply opens and closes. data-collapsible="mobile" keeps the
   * panel permanently open from the md breakpoint up.
   */
  class DetailsDisclosure extends HTMLElement {
    constructor() {
      super();
      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.syncBreakpoint = this.syncBreakpoint.bind(this);
    }

    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.details ? this.details.querySelector('summary') : null;
      this.panel = this.summary ? this.summary.nextElementSibling : null;
      if (!this.panel) return;

      this.summary.addEventListener('click', this.onSummaryClick);

      if (this.dataset.collapsible === 'mobile') {
        if (this.openOnMobile === undefined) this.openOnMobile = this.details.hasAttribute('open');
        this.syncBreakpoint();
        window.addEventListener('on:breakpoint-change', this.syncBreakpoint);
      }
    }

    disconnectedCallback() {
      if (this.summary) this.summary.removeEventListener('click', this.onSummaryClick);
      window.removeEventListener('on:breakpoint-change', this.syncBreakpoint);
    }

    get isOpen() {
      return Boolean(this.details && this.details.open && !this.details.classList.contains('is-closing'));
    }

    get isStatic() {
      return this.classList.contains('is-static');
    }

    onSummaryClick(event) {
      event.preventDefault();
      if (this.isStatic) return;
      this.toggle();
    }

    syncBreakpoint() {
      const wide = window.matchMedia(theme.mediaQueries.md).matches;
      this.classList.toggle('is-static', wide);
      this.details.classList.remove('is-closing');
      this.details.open = wide || this.openOnMobile;
      if (wide) {
        this.summary.setAttribute('tabindex', '-1');
      } else {
        this.summary.removeAttribute('tabindex');
      }
    }

    toggle(force) {
      const open = typeof force === 'boolean' ? force : !this.isOpen;
      return open ? this.open() : this.close();
    }

    open() {
      if (!this.panel || this.isOpen) return Promise.resolve();
      this.details.classList.remove('is-closing');
      this.details.open = true;
      return theme.slideToggle(this.panel, true, { keepVisible: true });
    }

    close() {
      if (!this.panel || !this.isOpen) return Promise.resolve();
      const { details, panel } = this;
      details.classList.add('is-closing');
      return theme.slideToggle(panel, false, { keepVisible: true }).then(() => {
        // Still closing? (the user may have re-opened it mid animation)
        if (!details.classList.contains('is-closing')) return;
        details.open = false;
        details.classList.remove('is-closing');
      });
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Overlays: shared behaviour of <modal-dialog> and <side-drawer>          */
  /* ---------------------------------------------------------------------- */

  /** Open overlays, top-most last. */
  const overlayStack = [];

  /** True when an ancestor would trap a fixed element (clip it or pin it below the page). */
  const isTrapped = (element) => {
    for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (
        style.transform !== 'none' ||
        style.filter !== 'none' ||
        (style.backdropFilter && style.backdropFilter !== 'none') ||
        style.perspective !== 'none' ||
        (style.contain && /paint|layout|strict|content/.test(style.contain)) ||
        (style.containerType && style.containerType !== 'normal') ||
        (style.willChange && /transform|filter|perspective/.test(style.willChange)) ||
        style.position === 'fixed' ||
        style.position === 'sticky' ||
        (style.position !== 'static' && style.zIndex !== 'auto') ||
        Number(style.opacity) < 1 ||
        style.isolation === 'isolate'
      ) {
        return true;
      }
    }
    return false;
  };

  /** Nearest modal / drawer an element sits in. */
  const overlayOf = (node) => {
    for (let current = node; current; current = current.parentElement) {
      // eslint-disable-next-line no-use-before-define
      if (current instanceof OverlayElement) return current;
    }
    return null;
  };

  class OverlayElement extends HTMLElement {
    constructor() {
      super();
      this.state = 'closed';
      this.opener = null;
      this.onClick = this.onClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
    }

    /** Event namespace: on:<kind>:before-open … */
    get kind() {
      return 'overlay';
    }

    get isOpen() {
      return this.state === 'open' || this.state === 'opening';
    }

    connectedCallback() {
      if (this.relocating) return;
      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerdown', this.onPointerDown);
    }

    disconnectedCallback() {
      if (this.relocating) return;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerdown', this.onPointerDown);
      // Removed from the page while open (section re-render): tidy up instantly.
      if (this.state !== 'closed') this.teardown();
    }

    onPointerDown(event) {
      this.pressTarget = event.target;
    }

    onClick() {}

    /** Whether the element has to move to <body> while it is open. */
    needsPortal() {
      const mode = this.dataset.portal;
      if (mode === 'always') return true;
      if (mode === 'never') return false;
      return isTrapped(this);
    }

    mount() {
      if (this.parentElement === document.body || !this.needsPortal()) return;
      this.home = document.createComment(` ${this.localName}${this.id ? ` #${this.id}` : ''} `);
      this.relocating = true;
      this.before(this.home);
      document.body.appendChild(this);
      this.relocating = false;
    }

    unmount() {
      const { home } = this;
      if (!home) return;
      this.home = null;
      this.relocating = true;
      if (home.isConnected) {
        home.replaceWith(this);
        this.relocating = false;
      } else {
        // The section it came from was re-rendered: a fresh copy already exists there.
        this.remove();
        this.relocating = false;
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('pointerdown', this.onPointerDown);
      }
    }

    emit(stage) {
      const detail = { id: this.id, opener: this.opener };
      theme.emit(`on:${this.kind}:${stage}`, detail, this);
      const alias = this.dataset.name;
      if (alias && alias !== this.kind) theme.emit(`on:${alias}:${stage}`, detail, this);
    }

    showBackdrop() {}

    hideBackdrop() {}

    /**
     * Opens the overlay.
     * @param {Element} [opener] - Focus returns here on close.
     * @param {Element} [focusTarget] - Element to focus inside (defaults to [autofocus] or the overlay).
     * @returns {Promise<void>} Resolves once the opening transition has finished.
     */
    open(opener, focusTarget) {
      if (this.state === 'open' || this.state === 'opening') return this.opening || Promise.resolve();
      if (this.state === 'closing') return this.closing.then(() => this.open(opener, focusTarget));
      if (!this.isConnected) return Promise.resolve();
      this.state = 'opening';
      this.opening = this.runOpen(opener, focusTarget);
      return this.opening;
    }

    async runOpen(opener, focusTarget) {
      this.opener = opener instanceof Element ? opener : document.activeElement;
      this.emit('before-open');

      // A drawer replaces other drawers unless it was opened from inside one.
      if (this.kind === 'drawer') {
        overlayStack
          .filter((other) => other !== this && other.kind === 'drawer' && !(this.opener && other.contains(this.opener)))
          .forEach((other) => other.close({ returnFocus: false }));
      }

      this.mount();
      this.hidden = false;
      this.showBackdrop();
      theme.lockScroll();
      overlayStack.push(this);

      // Let the browser paint the closed state first so the transition runs.
      this.getBoundingClientRect();
      await nextFrame();
      if (this.state !== 'opening') return;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');

      const target = focusTarget || this.querySelector('[autofocus]') || this;
      theme.trapFocus(this, target);
      this.onOpen();

      await wait(this.transitionMs());
      if (this.state !== 'opening') return;
      this.state = 'open';
      this.emit('after-open');
    }

    /**
     * Closes the overlay.
     * @param {object} [options] - { returnFocus: boolean } (default true)
     * @returns {Promise<void>} Resolves once the closing transition has finished.
     */
    close(options = {}) {
      if (this.state === 'closed' || this.state === 'closing') return this.closing || Promise.resolve();
      this.closing = this.runClose(options);
      return this.closing;
    }

    async runClose({ returnFocus = true } = {}) {
      this.state = 'closing';
      this.emit('before-close');
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.hideBackdrop();
      this.releaseStack(returnFocus);

      await wait(this.transitionMs());
      if (this.state !== 'closing') return;
      this.finishClose();
      this.emit('after-close');
    }

    releaseStack(returnFocus) {
      const index = overlayStack.indexOf(this);
      const wasTop = index === overlayStack.length - 1;
      if (index > -1) overlayStack.splice(index, 1);
      if (!wasTop) return;

      const opener = this.opener && this.opener.isConnected ? this.opener : null;
      const below = overlayStack[overlayStack.length - 1];
      if (below) {
        theme.trapFocus(below, opener && below.contains(opener) ? opener : below);
      } else {
        theme.releaseFocus(returnFocus ? opener : undefined);
      }
    }

    finishClose() {
      this.state = 'closed';
      this.hidden = true;
      this.removeBackdrop();
      theme.unlockScroll();
      theme.pauseAllMedia(this);
      this.unmount();
      this.onClose();
    }

    /** Instant close used when the element leaves the DOM while open. */
    teardown() {
      const active = this.state !== 'closed';
      const wasListed = overlayStack.includes(this);
      this.state = 'closed';
      this.classList.remove('is-open');
      this.hidden = true;
      this.removeBackdrop();
      if (wasListed) this.releaseStack(false);
      if (active) theme.unlockScroll();
      if (this.home) {
        this.home.remove();
        this.home = null;
      }
    }

    removeBackdrop() {}

    transitionMs() {
      const time = motionAllowed() ? transitionTime(this) : 0;
      return time ? time + 30 : 0;
    }

    /** Hooks for subclasses. */
    onOpen() {}

    onClose() {}
  }

  // Escape closes the top-most overlay only.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    const top = overlayStack[overlayStack.length - 1];
    if (!top || top.dataset.dismissible === 'false') return;
    event.preventDefault();
    top.close();
  });

  // Theme editor: the section an open overlay came from is being replaced.
  document.addEventListener('shopify:section:unload', (event) => {
    overlayStack.slice().forEach((overlay) => {
      if (overlay.home && event.target.contains(overlay.home)) {
        overlay.teardown();
        overlay.relocating = true;
        overlay.remove();
        overlay.relocating = false;
      }
    });
  });

  /* ---------------------------------------------------------------------- */
  /* <modal-dialog> + <modal-opener>                                         */
  /* ---------------------------------------------------------------------- */

  class ModalDialog extends OverlayElement {
    get kind() {
      return 'modal';
    }

    needsPortal() {
      return this.dataset.portal !== 'never';
    }

    onClick(event) {
      const closer = event.target.closest('[data-modal-close]');
      if (closer && overlayOf(closer) === this) {
        event.preventDefault();
        this.close();
        return;
      }
      // Click on the dimmed area (the element itself), not a drag that ended there.
      if (event.target === this && this.pressTarget === this && this.dataset.dismissible !== 'false') this.close();
    }

    onOpen() {
      theme.pauseAllMedia(document);
      // Wide tables scroll inside the dialog instead of stretching it.
      this.querySelectorAll('.modal__content table').forEach((table) => {
        if (table.parentElement.classList.contains('modal__table-scroll')) return;
        const scroller = document.createElement('div');
        scroller.className = 'modal__table-scroll';
        table.before(scroller);
        scroller.appendChild(table);
      });
    }
  }

  class ModalOpener extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      const trigger = this.querySelector('button, a, [role="button"]');
      if (trigger && !trigger.hasAttribute('aria-haspopup')) trigger.setAttribute('aria-haspopup', 'dialog');
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const trigger = event.target.closest('button, a, [role="button"]');
      if (!trigger || !this.contains(trigger)) return;
      const modal = document.getElementById(this.dataset.modal);
      if (!modal || typeof modal.open !== 'function') return;
      event.preventDefault();
      modal.open(trigger);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* <side-drawer>                                                           */
  /* ---------------------------------------------------------------------- */

  class SideDrawer extends OverlayElement {
    constructor() {
      super();
      this.onBackdropClick = this.onBackdropClick.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onEditorSelect = this.onEditorSelect.bind(this);
      this.onEditorDeselect = this.onEditorDeselect.bind(this);
    }

    get kind() {
      return 'drawer';
    }

    get side() {
      return this.dataset.side || 'end';
    }

    connectedCallback() {
      super.connectedCallback();
      if (this.relocating) return;
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });

      // data-open-on-select: show the drawer while its section is selected in the theme editor.
      if (window.Shopify && Shopify.designMode && this.hasAttribute('data-open-on-select')) {
        this.editorSection = this.closest('.shopify-section');
        if (this.editorSection) {
          this.editorSection.addEventListener('shopify:section:select', this.onEditorSelect);
          this.editorSection.addEventListener('shopify:section:deselect', this.onEditorDeselect);
        }
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.relocating) return;
      this.removeEventListener('touchstart', this.onTouchStart);
      if (this.editorSection) {
        this.editorSection.removeEventListener('shopify:section:select', this.onEditorSelect);
        this.editorSection.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
        this.editorSection = null;
      }
    }

    onEditorSelect() {
      this.open();
    }

    onEditorDeselect() {
      this.close({ returnFocus: false });
    }

    onClick(event) {
      const closer = event.target.closest('[data-drawer-close]');
      if (!closer || overlayOf(closer) !== this) return;
      // A close control that names another drawer is handled by the document listener.
      if (closer.dataset.drawerClose && closer.dataset.drawerClose !== this.id) return;
      event.preventDefault();
      this.close();
    }

    showBackdrop() {
      if (this.backdrop) return;
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'backdrop drawer-backdrop';
      this.backdrop.addEventListener('click', this.onBackdropClick);
      this.before(this.backdrop);
      this.backdrop.getBoundingClientRect();
      requestAnimationFrame(() => this.backdrop && this.state !== 'closing' && this.backdrop.classList.add('is-visible'));
    }

    hideBackdrop() {
      if (this.backdrop) this.backdrop.classList.remove('is-visible');
    }

    removeBackdrop() {
      if (!this.backdrop) return;
      this.backdrop.removeEventListener('click', this.onBackdropClick);
      this.backdrop.remove();
      this.backdrop = null;
    }

    onBackdropClick() {
      if (this.dataset.dismissible !== 'false') this.close();
    }

    /* Bottom sheets can be flicked down by their header / handle. */
    onTouchStart(event) {
      if (this.side !== 'bottom' || this.state !== 'open' || event.touches.length !== 1) return;
      const grip = event.target === this ? this : event.target.closest('.drawer__header, .drawer__handle');
      if (!grip || !this.contains(grip) || event.target.closest('button, a, input, select, textarea')) return;
      this.swipe = { y: event.touches[0].clientY, delta: 0, time: Date.now() };
      this.addEventListener('touchmove', this.onTouchMove, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd);
      this.addEventListener('touchcancel', this.onTouchEnd);
    }

    onTouchMove(event) {
      if (!this.swipe) return;
      this.swipe.delta = Math.max(0, event.touches[0].clientY - this.swipe.y);
      this.style.transition = 'none';
      this.style.transform = `translateY(${this.swipe.delta}px)`;
    }

    onTouchEnd() {
      this.removeEventListener('touchmove', this.onTouchMove);
      this.removeEventListener('touchend', this.onTouchEnd);
      this.removeEventListener('touchcancel', this.onTouchEnd);
      const { swipe } = this;
      this.swipe = null;
      if (!swipe) return;
      const fast = swipe.delta > 40 && Date.now() - swipe.time < 250;
      this.style.transition = '';
      this.style.transform = '';
      if (swipe.delta > this.offsetHeight / 3 || fast) this.close();
    }
  }

  // Declarative triggers, wherever they are (and whenever they are added).
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;

    const drawerOpener = event.target.closest('[data-drawer-open]');
    if (drawerOpener) {
      const drawer = document.getElementById(drawerOpener.dataset.drawerOpen);
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open(drawerOpener);
      }
      return;
    }

    const drawerCloser = event.target.closest('[data-drawer-close]');
    if (drawerCloser && drawerCloser.dataset.drawerClose) {
      const drawer = document.getElementById(drawerCloser.dataset.drawerClose);
      if (drawer && typeof drawer.close === 'function') {
        event.preventDefault();
        drawer.close();
      }
      return;
    }

    const modalOpener = event.target.closest('[data-modal-open]');
    if (modalOpener) {
      const modal = document.getElementById(modalOpener.dataset.modalOpen);
      if (modal && typeof modal.open === 'function') {
        event.preventDefault();
        modal.open(modalOpener);
      }
    }
  });

  /* ---------------------------------------------------------------------- */
  /* <carousel-slider>                                                       */
  /* ---------------------------------------------------------------------- */

  const SLIDER_CONTROLS = '[data-slider-prev], [data-slider-next], [data-slider-dot]';

  class CarouselSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.offsets = [];
      this.onScroll = this.onScroll.bind(this);
      this.refresh = this.refresh.bind(this);
      this.queueRefresh = this.queueRefresh.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-slider-viewport]');
      this.track = this.querySelector('[data-slider-track]');
      if (!this.viewport || !this.track) return;

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('click', this.onClickCapture, true);
      this.viewport.addEventListener('dragstart', this.onDragStart);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      window.addEventListener('on:debounced-resize', this.refresh);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.queueRefresh);
        this.resizeObserver.observe(this.viewport);
      }
      // Slides added / removed, or a slide's `hidden` flipped.
      this.mutationObserver = new MutationObserver((records) => {
        const relevant = records.some((record) =>
          record.type === 'childList' ? record.target === this.track : record.target.parentElement === this.track
        );
        if (relevant) this.queueRefresh();
      });
      this.mutationObserver.observe(this.track, { childList: true, attributes: true, attributeFilter: ['hidden'], subtree: true });

      this.refresh();
      this.classList.add('is-ready');

      const initial = Number(this.dataset.sliderInitial);
      if (initial > 0) this.goTo(initial, { instant: true });
    }

    disconnectedCallback() {
      if (!this.viewport) return;
      this.viewport.removeEventListener('scroll', this.onScroll);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('click', this.onClickCapture, true);
      this.viewport.removeEventListener('dragstart', this.onDragStart);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.removeEventListener('on:debounced-resize', this.refresh);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.refreshFrame);
      clearTimeout(this.settleTimer);
    }

    /** Visible slides, in order. */
    get slides() {
      return this.track ? Array.from(this.track.children).filter((slide) => !slide.hidden) : [];
    }

    /** False while the breakpoint in data-slider-active turns the slider into a grid. */
    get isActive() {
      if (!this.viewport) return false;
      const overflow = getComputedStyle(this.viewport).overflowX;
      return overflow === 'auto' || overflow === 'scroll';
    }

    get scrollPosition() {
      return Math.abs(this.viewport.scrollLeft);
    }

    get maxScroll() {
      return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    }

    get step() {
      return this.dataset.sliderStep === 'page' ? this.perPage : 1;
    }

    queueRefresh() {
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(this.refresh);
    }

    /** Re-measures everything. Call after slides were added, removed or resized. */
    refresh() {
      if (!this.viewport || !this.isConnected) return;
      const slides = this.slides;
      this.rtl = getComputedStyle(this.viewport).direction === 'rtl';
      this.active = this.isActive;

      const view = this.viewport.getBoundingClientRect();
      const style = getComputedStyle(this.viewport);
      const padding = parseFloat(this.rtl ? style.scrollPaddingRight : style.scrollPaddingLeft) || 0;
      const position = this.scrollPosition;

      // Scroll position (distance from the start) at which each slide is snapped.
      this.offsets = slides.map((slide) => {
        const rect = slide.getBoundingClientRect();
        const gap = this.rtl ? view.right - padding - rect.right : rect.left - view.left - padding;
        return Math.max(0, gap + position);
      });

      // Whole slides that fit in view: (width + gap) / (slide + gap).
      const slideWidth = slides.length ? slides[0].getBoundingClientRect().width : view.width;
      const span = slides.length > 1 ? this.offsets[1] - this.offsets[0] : slideWidth;
      const gap = Math.max(0, span - slideWidth);
      this.perPage = span > 0 ? Math.max(1, Math.floor((view.width - padding * 2 + gap + 1) / span)) : 1;
      this.scrollable = this.active && this.maxScroll > 1;

      this.parts = {
        prev: this.controls('[data-slider-prev]'),
        next: this.controls('[data-slider-next]'),
        dots: this.controls('[data-slider-dots]'),
        current: this.controls('[data-slider-current]'),
        total: this.controls('[data-slider-total]')
      };

      this.classList.toggle('is-scrollable', this.scrollable);
      this.buildDots(slides);
      this.update(true);
    }

    onScroll() {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.update());
    }

    update(silent) {
      const slides = this.slides;
      const position = this.scrollPosition;
      const max = this.maxScroll;
      const atStart = position <= 1;
      const atEnd = position >= max - 1;

      let index = 0;
      if (this.scrollable) {
        let best = Infinity;
        this.offsets.forEach((offset, i) => {
          const distance = Math.abs(Math.min(offset, max) - position);
          if (distance < best - 0.5) {
            best = distance;
            index = i;
          }
        });
      } else {
        index = clamp(this.currentIndex, 0, Math.max(0, slides.length - 1));
      }

      this.classList.toggle('is-at-start', !this.scrollable || atStart);
      this.classList.toggle('is-at-end', !this.scrollable || atEnd);

      const parts = this.parts || { prev: [], next: [], dots: [], current: [], total: [] };
      parts.prev.forEach((button) => {
        button.hidden = !this.scrollable;
        button.disabled = atStart;
      });
      parts.next.forEach((button) => {
        button.hidden = !this.scrollable;
        button.disabled = atEnd;
      });

      const stops = this.stopCount(slides.length);
      const stop = atEnd && this.scrollable ? stops - 1 : clamp(Math.round(index / this.step), 0, stops - 1);
      parts.dots.forEach((list) => {
        list.hidden = !this.scrollable || stops < 2;
        Array.from(list.children).forEach((dot, i) => {
          if (i === stop) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      });
      parts.current.forEach((node) => {
        node.textContent = index + 1;
      });
      parts.total.forEach((node) => {
        node.textContent = slides.length;
      });

      const changed = index !== this.currentIndex;
      this.currentIndex = index;
      slides.forEach((slide, i) => slide.classList.toggle('is-current', i === index));
      this.updateHeight(slides[index]);

      if (changed && !silent) {
        theme.emit('on:carousel-slider:select', { index, slide: slides[index] }, this);
      }
    }

    /** Number of positions the arrows / dots can stop at. */
    stopCount(total) {
      if (!this.scrollable) return 1;
      return Math.max(1, Math.ceil((total - this.perPage) / this.step) + 1);
    }

    buildDots(slides) {
      const stops = this.stopCount(slides.length);
      this.parts.dots.forEach((list) => {
        if (list.children.length === stops) return;
        const template = list.dataset.label || '';
        list.textContent = '';
        for (let i = 0; i < stops; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'slider-dots__dot';
          dot.dataset.sliderDot = String(i);
          if (this.id) dot.setAttribute('aria-controls', this.id);
          if (template) dot.setAttribute('aria-label', template.replace('[i]', i + 1).replace('[n]', stops));
          list.appendChild(dot);
        }
      });
    }

    /** With data-slider-adaptive-height the viewport follows the height of the current slide. */
    updateHeight(slide) {
      if (!this.hasAttribute('data-slider-adaptive-height')) return;
      if (!slide || !this.scrollable) {
        this.style.removeProperty('--slider-height');
        return;
      }
      const height = slide.offsetHeight;
      if (height) this.style.setProperty('--slider-height', `${height}px`);
      if (this.resizeObserver && this.observedSlide !== slide) {
        if (this.observedSlide) this.resizeObserver.unobserve(this.observedSlide);
        this.resizeObserver.observe(slide);
        this.observedSlide = slide;
      }
    }

    /**
     * Elements steering this slider: the ones inside it plus any element in the
     * page that points at it with aria-controls / data-slider-for.
     */
    controls(selector) {
      const found = Array.from(this.querySelectorAll(selector)).filter(
        (node) => node.closest('carousel-slider') === this && !node.hasAttribute('data-slider-for')
      );
      if (this.id) {
        const id = window.CSS && CSS.escape ? CSS.escape(this.id) : this.id;
        document
          .querySelectorAll(`${selector}[aria-controls="${id}"], ${selector}[data-slider-for="${id}"], [data-slider-for="${id}"] ${selector}`)
          .forEach((node) => {
            if (!found.includes(node)) found.push(node);
          });
      }
      return found;
    }

    /**
     * Scrolls to a slide.
     * @param {number|Element} target - Slide index or slide element.
     * @param {object} [options] - { instant: boolean }
     */
    goTo(target, options = {}) {
      const slides = this.slides;
      const index = typeof target === 'number' ? clamp(Math.round(target), 0, slides.length - 1) : slides.indexOf(target);
      if (index < 0 || !slides.length) return;

      if (!this.scrollable) {
        if (index !== this.currentIndex) {
          this.currentIndex = index;
          this.update(true);
          theme.emit('on:carousel-slider:select', { index, slide: slides[index] }, this);
        }
        return;
      }

      const left = Math.min(this.offsets[index] || 0, this.maxScroll);
      this.viewport.scrollTo({
        left: this.rtl ? -left : left,
        behavior: options.instant || !motionAllowed() ? 'auto' : 'smooth'
      });
    }

    next() {
      this.goTo(Math.min(this.currentIndex + this.step, this.slides.length - 1));
    }

    prev() {
      // From the very end the "current" slide may be several steps back already.
      const atEnd = this.scrollPosition >= this.maxScroll - 1;
      const from = atEnd ? Math.min(this.currentIndex, this.lastStopIndex()) : this.currentIndex;
      this.goTo(Math.max(from - this.step, 0));
    }

    lastStopIndex() {
      const max = this.maxScroll;
      const index = this.offsets.findIndex((offset) => offset >= max - 1);
      return index < 0 ? this.offsets.length - 1 : index;
    }

    /** Scrolls only as far as needed to bring a slide fully into view (thumbnail strips). */
    reveal(target, options = {}) {
      const slides = this.slides;
      const slide = typeof target === 'number' ? slides[target] : target;
      if (!slide || !this.scrollable) return;
      const view = this.viewport.getBoundingClientRect();
      const rect = slide.getBoundingClientRect();
      if (rect.left >= view.left - 1 && rect.right <= view.right + 1) return;
      const delta = rect.left < view.left ? rect.left - view.left : rect.right - view.right;
      this.viewport.scrollBy({ left: delta, behavior: options.instant || !motionAllowed() ? 'auto' : 'smooth' });
    }

    onKeydown(event) {
      if (event.defaultPrevented || !this.scrollable) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target.closest('input, select, textarea, [contenteditable], [role="listbox"], [role="slider"], video, audio')) return;
      if (event.target.closest('carousel-slider') !== this) return;
      event.preventDefault();
      const forward = (event.key === 'ArrowRight') !== this.rtl;
      if (forward) {
        this.next();
      } else {
        this.prev();
      }
    }

    onBlockSelect(event) {
      const slide = this.slides.find((item) => item.contains(event.target));
      if (slide) this.goTo(slide);
    }

    /* -- Grab to drag with a mouse ---------------------------------------- */

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || !this.scrollable) return;
      if (event.target.closest('input, select, textarea, [data-slider-no-drag]')) return;
      this.drag = { x: event.clientX, left: this.viewport.scrollLeft, moved: false };
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
    }

    onPointerMove(event) {
      const { drag } = this;
      if (!drag) return;
      const delta = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(delta) < 6) return;
        drag.moved = true;
        clearTimeout(this.settleTimer);
        this.classList.add('is-dragging');
        const selection = window.getSelection ? window.getSelection() : null;
        if (selection) selection.removeAllRanges();
      }
      this.viewport.scrollLeft = drag.left - delta;
    }

    onPointerUp(event) {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      const { drag } = this;
      this.drag = null;
      if (!drag || !drag.moved) return;

      // Swallow the click that follows the release.
      this.dragged = true;
      setTimeout(() => {
        this.dragged = false;
      }, 0);

      // Settle on the slide the drag was heading for, then give snapping back.
      const travelled = event.clientX - drag.x;
      const forward = this.rtl ? travelled > 0 : travelled < 0;
      const position = this.scrollPosition;
      const max = this.maxScroll;
      let index = this.offsets.findIndex((offset) => Math.min(offset, max) >= position - 1);
      if (index < 0) index = this.offsets.length - 1;
      if (!forward && index > 0 && Math.min(this.offsets[index], max) > position + 1) index -= 1;
      this.goTo(index);
      this.settleTimer = setTimeout(() => this.classList.remove('is-dragging'), motionAllowed() ? 450 : 0);
    }

    onClickCapture(event) {
      if (!this.dragged) return;
      event.preventDefault();
      event.stopPropagation();
    }

    onDragStart(event) {
      if (this.scrollable && window.matchMedia('(pointer: fine)').matches) event.preventDefault();
    }
  }

  const sliderFor = (control) => {
    const scope = control.closest('[data-slider-for]');
    const id = control.getAttribute('aria-controls') || (scope ? scope.dataset.sliderFor : '');
    const slider = id ? document.getElementById(id) : control.closest('carousel-slider');
    return slider instanceof CarouselSlider ? slider : null;
  };

  document.addEventListener('click', (event) => {
    const control = event.target.closest(SLIDER_CONTROLS);
    if (!control) return;
    const slider = sliderFor(control);
    if (!slider) return;
    event.preventDefault();
    if (control.hasAttribute('data-slider-prev')) {
      slider.prev();
    } else if (control.hasAttribute('data-slider-next')) {
      slider.next();
    } else {
      slider.goTo(Math.min(Number(control.dataset.sliderDot) * slider.step, slider.lastStopIndex()));
    }
  });

  /* ---------------------------------------------------------------------- */
  /* <quantity-wrapper>                                                      */
  /* ---------------------------------------------------------------------- */

  class QuantityWrapper extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.sync = this.sync.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.sync);
      this.addEventListener('input', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.sync);
      this.removeEventListener('input', this.sync);
    }

    get input() {
      return this.querySelector('input');
    }

    limits(input) {
      const step = Number(input.step) > 0 ? Number(input.step) : 1;
      const min = input.min !== '' && !Number.isNaN(Number(input.min)) ? Number(input.min) : 1;
      const max = input.max !== '' && !Number.isNaN(Number(input.max)) ? Number(input.max) : Infinity;
      return { step, min, max };
    }

    onClick(event) {
      const button = event.target.closest('[data-qty-minus], [data-qty-plus]');
      const { input } = this;
      if (!button || !input || input.disabled) return;
      event.preventDefault();

      const { step, min, max } = this.limits(input);
      const current = Number(input.value);
      const base = Number.isNaN(current) ? min : current;
      const direction = button.hasAttribute('data-qty-plus') ? 1 : -1;
      const value = clamp(base + direction * step, min, max);
      if (value === current) {
        this.sync();
        return;
      }

      input.value = String(value);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /** Greys out − / + at the limits. */
    sync() {
      const { input } = this;
      if (!input) return;
      const { min, max } = this.limits(input);
      const value = Number(input.value);
      // aria-disabled, not disabled: the buttons must keep working if a script
      // changes the value behind our back.
      const mark = (button, off) => {
        if (!button) return;
        button.classList.toggle('is-disabled', off);
        button.setAttribute('aria-disabled', String(off));
      };
      mark(this.querySelector('[data-qty-minus]'), input.disabled || value <= min);
      mark(this.querySelector('[data-qty-plus]'), input.disabled || value >= max);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* <toggle-target>                                                         */
  /* ---------------------------------------------------------------------- */
  /*
   * <toggle-target><button aria-controls="Panel" aria-expanded="false"
   *   data-label-close="Hide">Show</button></toggle-target>
   * <div id="Panel" class="toggle-panel" hidden>…</div>
   */
  class ToggleTarget extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('button, [role="button"]');
      if (!this.trigger) {
        // No button inside: the element itself becomes the control.
        this.trigger = this;
        if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
        if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
        this.addEventListener('keydown', this.onKeydown);
      }
      this.labelNode = this.trigger.querySelector('[data-toggle-label]') || this.trigger;
      if (this.trigger.dataset.labelClose && !this.trigger.dataset.labelOpen) {
        this.trigger.dataset.labelOpen = this.labelNode.textContent.trim();
      }
      this.addEventListener('click', this.onClick);
      this.render(this.isOpen);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }

    get target() {
      const id = this.trigger.getAttribute('aria-controls') || this.dataset.target;
      return id ? document.getElementById(id) : null;
    }

    get isOpen() {
      const { target } = this;
      return Boolean(target && !target.hidden);
    }

    onClick(event) {
      if (this.trigger !== this && !this.trigger.contains(event.target)) return;
      event.preventDefault();
      this.toggle();
    }

    onKeydown(event) {
      if (event.target !== this || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      this.toggle();
    }

    render(open) {
      this.trigger.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-open', open);
      const { labelOpen, labelClose } = this.trigger.dataset;
      if (labelClose && labelOpen) this.labelNode.textContent = open ? labelClose : labelOpen;
    }

    toggle(force) {
      const { target } = this;
      if (!target) return Promise.resolve();
      const open = typeof force === 'boolean' ? force : target.hidden;
      if (open === !target.hidden && !animations.has(target)) return Promise.resolve();
      this.render(open);
      theme.emit('on:toggle-target:change', { open, target }, this);
      return theme.slideToggle(target, open).then(() => {
        if (open && this.dataset.focus) {
          const field = target.querySelector(this.dataset.focus);
          if (field) field.focus();
        }
      });
    }

    open() {
      return this.toggle(true);
    }

    close() {
      return this.toggle(false);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* <link-dropdown>                                                         */
  /* ---------------------------------------------------------------------- */
  /*
   * <link-dropdown class="link-dropdown">
   *   <button class="link-dropdown__toggle" aria-expanded="false" aria-controls="Menu">…</button>
   *   <div class="link-dropdown__menu" id="Menu" hidden><a class="link-dropdown__item" href>…</a></div>
   * </link-dropdown>
   */
  class LinkDropdown extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onOutside = this.onOutside.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('button');
      this.menu = this.button ? document.getElementById(this.button.getAttribute('aria-controls')) || this.button.nextElementSibling : null;
      if (!this.menu) return;
      // Closed menus are taken out of the page until they are opened.
      if (this.button.getAttribute('aria-expanded') !== 'true') {
        this.button.setAttribute('aria-expanded', 'false');
        this.menu.hidden = true;
      }
      this.button.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('click', this.onOutside);
    }

    get isOpen() {
      return this.button.getAttribute('aria-expanded') === 'true';
    }

    get items() {
      return Array.from(this.menu.querySelectorAll('a[href], button:not([disabled])'));
    }

    onClick(event) {
      event.preventDefault();
      this.toggle();
    }

    onOutside(event) {
      if (!this.contains(event.target)) this.close();
    }

    onFocusOut(event) {
      if (this.isOpen && event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
    }

    onKeydown(event) {
      const { items } = this;
      const index = items.indexOf(document.activeElement);

      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close();
        this.button.focus();
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!items.length) return;
        event.preventDefault();
        if (!this.isOpen) this.open();
        const offset = event.key === 'ArrowDown' ? 1 : -1;
        const next = index < 0 ? (offset > 0 ? 0 : items.length - 1) : (index + offset + items.length) % items.length;
        items[next].focus();
      } else if ((event.key === 'Home' || event.key === 'End') && this.isOpen && items.length) {
        event.preventDefault();
        items[event.key === 'Home' ? 0 : items.length - 1].focus();
      }
    }

    toggle(force) {
      const open = typeof force === 'boolean' ? force : !this.isOpen;
      if (open === this.isOpen) return;
      this.button.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-open', open);
      this.menu.hidden = !open;
      if (open) {
        document.addEventListener('click', this.onOutside);
      } else {
        document.removeEventListener('click', this.onOutside);
      }
    }

    open() {
      this.toggle(true);
    }

    close() {
      this.toggle(false);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Hint bubble                                                             */
  /* ---------------------------------------------------------------------- */

  /**
   * Shows a short message in a bubble above a control, then removes it.
   * @param {string} message - Plain text.
   * @param {Element} anchor - Element the bubble points at.
   * @param {object} [options] - { duration: ms (default 3500) }
   * @returns {HTMLElement} The bubble.
   */
  theme.showHint = (message, anchor, options = {}) => {
    document.querySelectorAll('.hint-bubble').forEach((old) => old.remove());

    const bubble = document.createElement('div');
    bubble.className = 'hint-bubble';
    bubble.setAttribute('role', 'alert');
    bubble.textContent = message;

    // Stay above the drawer / modal the anchor sits in.
    const host = (anchor && anchor.closest('.drawer, .modal')) || document.body;
    host.appendChild(bubble);

    const rect = anchor ? anchor.getBoundingClientRect() : { left: window.innerWidth / 2, top: 80, width: 0, bottom: 80 };
    const width = bubble.offsetWidth;
    const height = bubble.offsetHeight;
    const left = clamp(rect.left + rect.width / 2 - width / 2, 8, Math.max(8, window.innerWidth - width - 8));
    const above = rect.top - height - 10;
    bubble.style.left = `${left}px`;
    bubble.style.top = `${above < 8 ? rect.bottom + 10 : above}px`;
    bubble.classList.toggle('hint-bubble--below', above < 8);

    requestAnimationFrame(() => bubble.classList.add('is-visible'));
    setTimeout(() => {
      bubble.classList.remove('is-visible');
      setTimeout(() => bubble.remove(), 300);
    }, options.duration || 3500);

    return bubble;
  };

  /* ---------------------------------------------------------------------- */
  /* Registration                                                            */
  /* ---------------------------------------------------------------------- */

  theme.DetailsDisclosure = DetailsDisclosure;
  theme.ModalDialog = ModalDialog;
  theme.SideDrawer = SideDrawer;
  theme.CarouselSlider = CarouselSlider;

  theme.define('details-disclosure', DetailsDisclosure);
  theme.define('modal-dialog', ModalDialog);
  theme.define('modal-opener', ModalOpener);
  theme.define('side-drawer', SideDrawer);
  theme.define('carousel-slider', CarouselSlider);
  theme.define('quantity-wrapper', QuantityWrapper);
  theme.define('toggle-target', ToggleTarget);
  theme.define('link-dropdown', LinkDropdown);
})();
